import React from 'react';
import { SDK, SDKEventPayload, SDKMessage, SDKUserContext, SDKStage, SDKStageType, SDKEngagementStatus, SDKParticipantType, SDKHumanParticipantType, SDKEngagement, SDKContextType as SDKCtxt, SDKContextType } from '../models/sdk';

import { ProviderParams } from '../models/react';
import { useLanguageContext } from './language';
import { useProcessingContext } from './processing';
import { useStatusContext } from './status';
import { useEngagementContext } from './engagement';
import { useNewMsgContext } from './msg';
import { MSGPartyType } from '../models/msg';
import { StatusContextType } from '../models/status';
import { UserDefaultType } from '../models/user';
import { EnvironmentConfig } from '../models/environment';
import { useToggleContext } from './toggle';
import { useUserContext } from './user';
import { LanguageCode, LanguageSession } from '../models/language';
import { MESSAGE_ENGAGEMENT_TERMINATED, MESSAGE_FAILED_ENGAGEMENT, MESSAGE_FAILURE, MESSAGE_UNAUTHORIZED_SYSTEM, MESSAGE_UNAUTHORIZED_USER } from '../constants';
import { AuthResponse, AuthResponseJwt } from '../models/auth';
import getAuth from '../axp/auth';
import { getConfig, getParams } from '../axp/config';
import firestore from '../axp/firestore';
import { isRetryAllowed } from '../axp/stages';
import { useOmniPageContext } from './omnipage';

declare const AvayaCCaaSChatSDK: SDK;
export const stage: SDKStage = {
    state: SDKStageType.IDLE,
    setState: function (state: SDKStageType) {
        this.state = state;
    },
    ongoing: null,
    setExpectation: function (state: SDKStageType) {
        this.ongoing = this.state;
        this.state = state;
    },
    clearExpectation: function () {
        if (!this.ongoing) return;

        this.state = this.ongoing;
        this.ongoing = null;
    },
};


const SDKContext = React.createContext<SDKContextType|undefined>(undefined);

export function useSDKContext() {
    return React.useContext(SDKContext);
}

export default function SDKProvider({ children }: ProviderParams) {
    const languageCtxt = useLanguageContext();
    if (!languageCtxt){
        throw new Error('Missing Notification context'); 
    }
    const processingCtxt = useProcessingContext();
    if (!processingCtxt){
        throw new Error("Missing Processing context");
    }
    const statusCtxt = useStatusContext();
    if (!statusCtxt){
        throw new Error("Missing Status context");
    }
    const engagementCtxt = useEngagementContext();
    if (!engagementCtxt){
        throw new Error("Missing Engagement context");
    }
    const newMsgCtxt = useNewMsgContext();
    if (!newMsgCtxt){
        throw new Error("Missing Message context");
    }
    const toggleCtxt = useToggleContext();
    if (!toggleCtxt){
        throw new Error("Missing Toggle context");
    } 
    const userCtxt = useUserContext();
    if (!userCtxt){
        throw new Error("Missing User context");
    }
    
    const omnipageCtxt = useOmniPageContext();
    if (!omnipageCtxt){
        throw new Error("Missing Omnipage context");
    }
    let isInError = false;
    let attempt = 1
    let maxAttempts = 5
    let participants = [] as String[];
    let isBusy = false;
    let languages: { user: LanguageCode, agent: LanguageCode } = {
        user: null,
        agent: "en-us"
    }
    let languageToggle = false;
    

    function updateTranslationStatus(value: boolean) {
            languageCtxt?.setStatus(value)
            languageToggle = value
    }

    const conversation = {
        hasStarted: false,
        start: (statusCtxt: StatusContextType | undefined) => {
            statusCtxt?.setIsOnline(true);
            conversation.hasStarted = true;
        },
        end: (statusCtxt: StatusContextType | undefined) => {
            statusCtxt?.setIsOnline(false);
            conversation.hasStarted = false;
        }
    }

    /**
     * Shuts down SDK.
     * If SDK is processing initialization shutdown will be executed ONLY after that.
     * Shutdown will be ignored if SDK isn't initialized.
     * */
    async function shutdownSDK() {
        if (languageCtxt) {languageCtxt.resetLanguage()}
        stage.setState(SDKStageType.SHUT_STARTED)
        await shutdownSession();
        omnipageCtxt?.removeTabEngagement();
        window.removeEventListener('onunload', beforeunload);
    }
    
    /**
     * Disconnects engagement if any and shuts down SDK.
     * */
    async function shutdownSession() {
        
        if (!processingCtxt) throw new Error('No processing context');
        if (!statusCtxt) throw new Error('No status context');
        if (!engagementCtxt) throw new Error('No engagement context');
        if (!newMsgCtxt) throw new Error('No new message context');
    
    
        console.debug('SDK shutdown initiated:', stage.state);
    
        stage.setState(SDKStageType.SHUT_STARTED);
    
        // DISCONNECT ENGAGEMENT
    
        console.debug('SDK engagement disconnection requested:', stage.state);
    
        stage.setState(SDKStageType.SHUT_ENGAGEMENT);
    
        try {
            let engagement: SDKEngagement = engagementCtxt.engagement as SDKEngagement
            // if (!engagement) {
            //     engagement = AvayaCCaaSChatSDK.getEngagementById(engagement.engagementId||"")
            // }
            let hasActive = (engagement?.engagementStatus !== SDKEngagementStatus.TERMINATED)
            if (hasActive) await engagement?.disconnect();
            else newMsgCtxt.addMessage(`No active engagement.`, MSGPartyType.UI, true);
            console.debug('SDK engagement disconnected:', stage.state);
        } catch (e) {
            console.warn('SDK got an exception on engagement disconnect', stage.state, e);
        }
    
        // if (isInterrupted('shutdown', SDKStageType.SHUT_ENGAGEMENT)) return;
    
        stage.setState(SDKStageType.SHUT_ENGAGEMENT_DISSCONNECTED);
    
        // SHUTDOWN SDK
    
        console.debug('SDK shutdown requested:', stage.state);
    
        try {
            stage.setState(SDKStageType.SHUT_REQUESTED);
            await AvayaCCaaSChatSDK.shutdown();
            stage.setState(SDKStageType.SHUT_EXECUTED);
        } catch {
            console.error('Exception in SDK Shutdown');
            stage.setState(SDKStageType.SHUT_FAILED);
        }
            shutdownUi();
    }
    
    /**
     * Locks UI and notifys user on SDK shutdown.
     * */
    function shutdownUi(
        prefix: string = '',
        isDone: boolean = true,
    ) {
        console.debug(prefix + 'SDK shutdown processed:', stage.state);
    
        newMsgCtxt?.addMessage('Current session was terminated.', MSGPartyType.UI, true);
        processingCtxt?.setIsFrozen(true);
        stage.setState(SDKStageType.SHUT_FINISHED);
        // engagementId = ""
        if (isDone) {
            conversation.end(statusCtxt);
            statusCtxt?.setAgent(undefined);
            processingCtxt?.setNotification({ message: '' });
            clearSDKListeners();
        };
    
        console.debug(prefix + 'SDK shutdown finished:', stage.state);
    }
    // INITILIAZE
    
    /**
     * Initializes SDK.
     * If SDK is processing shutdown initialization will be executed ONLY after that.
     * */
     async function initializeSDK(
        user: UserDefaultType | undefined,
        config: EnvironmentConfig,
        reengage: boolean = false
    ) {
        // CHECKUP
    
        if (!user) throw new Error('No user context');
    
        // BOT_NAME = config.bot.name;
        // env = config.env
        const default_queue = config.getQueue(user as UserDefaultType);
        // config_selected = config;
        const default_agent_language = config.getDefaultAgentLanguage(default_queue);
        // languages.user = user.default_language
        languageCtxt?.setUserLanguage(user.default_language);
    
        console.debug('SDK init queued:', stage.state);
    
        // ACTIONS
        await initializeSession(user,  config, reengage);

        omnipageCtxt?.setTabEngagement()
        engagementCtxt?.initEngagement()
        toggleCtxt?.stopLoading()
        window.addEventListener('onunload', beforeunload)
    }
    async function restartSDK(
        user: UserDefaultType,
        config: EnvironmentConfig,
        reengage: boolean = false
    ) {

        if (!user) throw new Error('No user context');
        processingCtxt?.setIsBusy(true);
        processingCtxt?.setIsFrozen(false);
        const sdkHooks = SDKHooks(user, config, reengage)
        newMsgCtxt?.addMessage(`Reconnecting...`, MSGPartyType.UI, true, '', true)
        console.debug('SDK init initiated:', stage.state);
        try {
            // AUTHORIZE USER
    
            if (!user) return onFailure(MESSAGE_UNAUTHORIZED_USER, SDKStageType.AUTH_DENIED);
    
            stage.setState(SDKStageType.AUTH_STARTED);
            newMsgCtxt?.addMessage(`Authenticating...`, MSGPartyType.UI, true, '', true)
    
            const { jwt, integrationId } = await getAuthResponseJwt(config, user)
            if (!jwt || !integrationId) return;
    
            console.debug(`Received\njwt:\n${jwt}\nintegrationId:\n${integrationId}`);
    
            console.debug('SDK authorized:', stage.state);
            stage.setState(SDKStageType.AUTH_FINISHED);
            newMsgCtxt?.addMessage(`Setting up the environment`, MSGPartyType.UI, true, '', true)
    
            recoverLanguageFromSession()
            // Initializing SDK hooks
    
            sdkHooks.initialization()
            sdkHooks.messaging()
            sdkHooks.participants()
            sdkHooks.engagement()
            sdkHooks.session()
            sdkHooks.auth()
            sdkHooks.shutdown()
    
    
            console.debug('SDK init requested:', stage.state);
    
            // if (!reengage) newMsgCtxt.addMessage(`Recovering last session...`, MSGPartyType.UI, true, '', true)
            stage.setState(SDKStageType.INIT_STARTED);
    
            stage.setState(SDKStageType.INIT_REQUESTED);
            await AvayaCCaaSChatSDK.init(getParams(config.url.axp.base, jwt, integrationId), getConfig());
            stage.setState(SDKStageType.INIT_EXECUTED);
            await engagementCtxt?.initEngagement();
            console.debug('SDK init executed:', stage.state);
        } catch (err: any) {
            handleSDKError(err, stage, config, user)
        }
        toggleCtxt?.stopLoading();

    }


    function getEngagementIdFromSessionStorage(){
        try{
            const engagementRetrieved = JSON.parse(sessionStorage.getItem("chat-widget-engagement") as string) as SDKEngagement
                if (engagementRetrieved && engagementRetrieved.engagementId){
                    return engagementRetrieved.engagementId 
                }else{
                    return '';
                }
        }
        catch(err){
            return ''
        }
    }
    // SDK Hooks 
    function SDKHooks(
        user: UserDefaultType,
        config: EnvironmentConfig,
        reengage: boolean = false
    ) {


        // Language Functions 
        function setLanguage(participant: SDKHumanParticipantType, language: LanguageCode) {
            console.log(`Changing ${participant} language to: ${language}`)
            if (participant === SDKParticipantType.AGENT) {
                languages.agent = language
                languageCtxt?.setAgentLanguage(language)
            }
            if (participant === SDKParticipantType.CUSTOMER) {
                languages.user = language
                languageCtxt?.setUserLanguage(language)
            }
        }

        // Engagement Functions
        async function validateEngagement(engagementId: string) {
            const engagement = await AvayaCCaaSChatSDK.getEngagementById(engagementId)
            if (!engagement) await shutdownSDK();
        }

        async function fetchEngagementInfo() {
            const url = `${config.url.api.engagementLanguage}`
            try {
                const response = await fetch(url, {
                    method: "POST", body: JSON.stringify({
                        "engagementId": engagementCtxt?.engagement?.engagementId || getEngagementIdFromSessionStorage()
                    })
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                return data
            } catch (error: any) {
                console.error('Error:', error.message);
            }
        }
        return {

            initialization: () => {
                AvayaCCaaSChatSDK.on('INITIALIZED', async (userContext: SDKUserContext) => {
                    console.debug('SDK init responded', stage.state);
                    let created
                    stage.setState(SDKStageType.INIT_ENGAGEMENT);
                    const engagementId_recovered = JSON.parse(sessionStorage.getItem("ccaasActiveEngagements") as string)

                    if (engagementCtxt?.engagement && engagementId_recovered) {
                        try {
                            created = await AvayaCCaaSChatSDK.getEngagementById(engagementId_recovered[0] || "");
                            // await created.join()
                        } catch (err: any) {
                            console.debug('Error while recovering engagement', err);
                        }
                    }
                    if (!created) {
                        created = await AvayaCCaaSChatSDK.createEngagement({
                            user_first_name: user.firstName,
                            user_last_name: user.lastName,
                            user_email: user.emailId,
                            companyId: user.companyId,
                            default_queue: config.default_queue,
                            environment: config.env,
                        });
                    }
                    stage.setState(SDKStageType.INIT_ENGAGEMENT_CREATED);
                    // if (engagementId === "") engagementId = created.engagementId;
                    if (created) {
                        if (!reengage) {
                            console.debug('SDK engagement created:', created);
                            newMsgCtxt?.addMessage('New session is waiting for the bot...', MSGPartyType.UI, true);
                            firestore(config.url.api.firestore, user, created.engagementId);
                        } else {
                            processingCtxt?.setIsBusy(false)
                            newMsgCtxt?.removeTempMessage()
                            toggleCtxt?.stopLoading()
                        }
                        stage.setState(SDKStageType.INIT_FINISHED);
                        processingCtxt?.setIsFrozen(false);
                        engagementCtxt?.setEngagement(created);
                        console.debug('SDK init finished:', stage.state);


                    } else {
                        console.error(MESSAGE_FAILED_ENGAGEMENT);
                        onFailure(MESSAGE_FAILED_ENGAGEMENT, stage.state);
                    }
                })
            },
            messaging: () => {
                AvayaCCaaSChatSDK.on('MESSAGE_ARRIVED', async (message: SDKMessage) => {
                    console.log('Message ARRIVED:', message);

                    // engagementId = message.engagementId
                    updateConversationStatus(reengage);
                    const participantType = message.senderParticipant.participantType;
                    if (SDKParticipantType.SYSTEM === participantType) {
                        newMsgCtxt?.addRichMessage(message);

                    }
                    if (SDKParticipantType.AGENT === participantType || SDKParticipantType.SUPERVISOR === participantType) {
                        
                        if (needToTranslate()) {
                            const translatedMessage = await languageCtxt?.translateMessageWithLanguages(message.body.elementText.text, SDKParticipantType.AGENT, languages.user, languages.agent)
                            newMsgCtxt?.addMessage(translatedMessage || message.body.elementText.text, participantType, false, message.senderParticipant.displayName, false,  message.body.elementText.text);
                            languageCtxt?.saveTranscript(translatedMessage || "error during translating this message" , message.messageId, SDKParticipantType.AGENT, user, engagementCtxt?.engagement?.engagementId || getEngagementIdFromSessionStorage())
                        } else {
                            newMsgCtxt?.addMessage(message.body.elementText.text, participantType, false, message.senderParticipant.displayName);
                        }

                    }

                    toggleCtxt?.stopLoading();
                    // ToDo check message.senderParticipant.displayName (it's empty for SYSTEM)
                    // validateEngagement(engagementCtxt?.engagement?.engagementId || "")

                });
                AvayaCCaaSChatSDK.on('MESSAGE_DELIVERED', async (message: SDKMessage) => {
                    console.log('Message DELIVERED:', message);
                    const participantType = message.senderParticipant.participantType;
                    if ('CUSTOMER' === participantType) {
                        const language = languageCtxt?.getLanguageByString(message.body.elementText.text)
                        if (language) {
                            setLanguage(SDKParticipantType.CUSTOMER, language)
                            languageCtxt?.setUserLanguage(language)
                        }
                        if (!needToTranslate()) newMsgCtxt?.addMessage(message.body.elementText.text, participantType, false, message.senderParticipant.displayName);
                    }
                    // ToDo check message.senderParticipant.displayName
                });
            },
            session: () => {
                AvayaCCaaSChatSDK.on('EVENT_STREAM_CONNECTING', (userContext: SDKUserContext) => {
                    console.log('EVENT_STREAM_CONNECTING', userContext)
                })

                AvayaCCaaSChatSDK.on('EVENT_STREAM_CONNECTED', (userContext: SDKUserContext) => {
                    console.log('EVENT_STREAM_CONNECTED', userContext)
                    processingCtxt?.setIsBusy(false)
                    if (isInError) {
                        newMsgCtxt?.removeTempMessage()
                        isInError = false
                    }
                    attempt = 0
                })

                AvayaCCaaSChatSDK.on('EVENT_STREAM_CLOSED', (userContext: SDKUserContext) => {
                    console.log('EVENT_STREAM_CLOSED', userContext)
                })

                AvayaCCaaSChatSDK.on('EVENT_STREAM_FAILED', async (eventPayload: SDKEventPayload) => {
                    console.error(`EVENT_STREAM_FAILED: ${JSON.stringify(eventPayload)}\n STATE: ${stage.state}`)
                    if (SDKStageType.SHUT_EXPECTED === stage.state) {
                        stage.clearExpectation();
                        processingCtxt?.setNotification({ message: '' });
                    } else {
                        console.log("Trying to connect...")
                        newMsgCtxt?.addMessage(`Trying to connect...`, MSGPartyType.UI, true);
                        isInError = true
                        if (isRetryAllowed(stage) && attempt <= maxAttempts) {
                            processingCtxt?.setIsBusy(true)
                            console.log(`Trying to connect... attempt ${attempt + 1}/${maxAttempts}`)
                            newMsgCtxt?.addMessage(`Trying to connect... attempt ${attempt + 1}/${maxAttempts}`, MSGPartyType.UI, true);
                            attempt += 1
                            AvayaCCaaSChatSDK.retryConnection()
                        }
                        if (attempt > maxAttempts) {
                            newMsgCtxt?.addMessage(`Something went wrong: Please verify your internet connection and refresh your browser`, MSGPartyType.UI, true);

                        }

                    }
                });

                AvayaCCaaSChatSDK.on('IDLE_TIMEOUT_EXPIRED', (eventPayload: SDKEventPayload) => {
                    console.log('IDLE_TIMEOUT_EXPIRED');
                    const gracePeriod = eventPayload.gracePeriod;
                    processingCtxt?.setNotification({
                        message: `If there are no new messages the chat session will expire in ${gracePeriod} seconds!`,
                        close: {
                            auto: gracePeriod,
                            onAuto: async (left: number) => {
                                newMsgCtxt?.addMessage('Current session expired.', MSGPartyType.UI);
                                newMsgCtxt?.addMessage('Shutting down...', MSGPartyType.UI, true);
                                await shutdownSession();
                            },
                            onManual: (left: number) => {
                                stage.setExpectation(SDKStageType.SHUT_EXPECTED);
                                newMsgCtxt?.addMessage(`Please, reply.\nOtherwise session will expire in ${left} seconds.`, MSGPartyType.UI);
                            },
                            onInterrupt: (left: number) => {
                                stage.setExpectation(SDKStageType.SHUT_EXPECTED);
                                newMsgCtxt?.addMessage(`Please, reply.\nOtherwise session will expire in ${left} seconds.`, MSGPartyType.UI);
                            }
                        }
                    });
                });
            },
            auth: () => {
                const refreshToken = () => refreshJWT(config, user);
                AvayaCCaaSChatSDK.on('TOKEN_EXPIRY_REMINDER', refreshToken);
                AvayaCCaaSChatSDK.on('TOKEN_EXPIRED', refreshToken);
            },
            participants: () => {
                // PARTICIPANT EVENTS
                AvayaCCaaSChatSDK.on('AGENT_PARTICIPANT_ADDED', (context: any) => {
                    console.log("AGENT_PARTICIPANT_ADDED:", context)
                })
                AvayaCCaaSChatSDK.on('PARTICIPANT_ADDED', async (userContext: SDKUserContext) => {
                    console.log('PARTICIPANT_ADDED', userContext);
                    if (SDKParticipantType.AGENT === userContext.participant.participantType) {
                        const engInfo = await fetchEngagementInfo()
                        if (engInfo?.data?.displayName && engInfo?.data?.apiKeyName && engInfo?.data?.languageCode) {
                            const lcode = languageCtxt?.getLanguageByString(engInfo.data.displayName) || languageCtxt?.getLanguageByString(engInfo.data.apiKeyName) || languageCtxt?.getLanguageByString(engInfo.data.languageCode)
                            setLanguage(SDKParticipantType.AGENT, lcode || engInfo.languageCode)
                            updateTranslationStatus(true)
                        } 
                        addParticipant(userContext);
                        processingCtxt?.setIsFrozen(false)
                    } if (SDKParticipantType.SUPERVISOR === userContext.participant.participantType) {
                        addParticipant(userContext);
                        processingCtxt?.setIsFrozen(false)
                    }
                });
                
                AvayaCCaaSChatSDK.on('PARTICIPANT_DISCONNECTED', (userContext: SDKUserContext) => {
                    console.log('PARTICIPANT_DISCONNECTED', userContext);
                    if (SDKParticipantType.AGENT === userContext.participant.participantType) {
                        removeParticipant(userContext);
                    }
                    if (SDKParticipantType.SUPERVISOR === userContext.participant.participantType) {
                        removeParticipant(userContext);
                    }
                            if (SDKParticipantType.SYSTEM === userContext.participant.participantType) {
                                if (isBusy) {
                                    newMsgCtxt?.addMessage('System disengaged.', MSGPartyType.UI);
                                    console.error('System disengaged');
                                    processingCtxt?.setIsFrozen(true);
                                    processingCtxt?.setIsBusy(false);
                                }
                                else {
                                    newMsgCtxt?.addMessage('Connecting...', MSGPartyType.UI, true);
                                    processingCtxt?.setIsFrozen(true)
                                }
                            }
                        });

            },
            engagement: () => {
                AvayaCCaaSChatSDK.on('ENGAGEMENT_ACTIVE', (userContext: SDKUserContext) => {
                    console.log('ENGAGEMENT_ACTIVE');
                });

                AvayaCCaaSChatSDK.on('ENGAGEMENT_ERROR', (userContext: SDKUserContext) => {
                    console.log('ENGAGEMENT_ERROR');
                });

                AvayaCCaaSChatSDK.on('ENGAGEMENT_TERMINATED', async (userContext: SDKUserContext) => {
                    console.log('ENGAGEMENT_TERMINATED');
                    processingCtxt?.setIsFrozen(true);
                    newMsgCtxt?.addMessage(MESSAGE_ENGAGEMENT_TERMINATED, MSGPartyType.UI, true);
                    engagementCtxt?.activateHistoryMode()
                });

            },
            shutdown: () => {

                AvayaCCaaSChatSDK.on('SHUTDOWN', (eventPayload: SDKEventPayload) => {
                    console.log('SHUTDOWN');
                    if (SDKStageType.SHUT_REQUESTED === stage.state) return;
                    shutdownUi('Auto');
                });
            }
        }

    }

    async function handleSDKError(
        err: any,
        stage: { state: SDKStageType },
        config: EnvironmentConfig,
        user: UserDefaultType
    ): Promise<void> {
        const error = typeof err === 'object' ? JSON.stringify(err) : err,
            msg = error.match(/ACS-\d*/),
            code = msg ? msg[0] : 'NONE';
        toggleCtxt?.stopLoading();
        switch (code) {
            case 'ACS-204': console.warn(`Warning: Duplicate attempt to initialize SDK:\n${error}`);
                console.warn('SDK ACS-204:', stage.state);
                await AvayaCCaaSChatSDK.shutdown();
                shutdownUi()
                newMsgCtxt?.addMessage('Restarting...', MSGPartyType.UI, true)
                cleanSessionStorage();
                initializeSession(user, config);
                break;

            case 'ACS-302': console.error(`Error: User is not logged in:\n${error}`);
                onFailure(MESSAGE_UNAUTHORIZED_USER, SDKStageType.AUTH_DENIED)
                break;

            case 'ACS-303':
                console.warn(`Error: Invalid configuration values: \n${error}`);
                break;

            case 'ACS-304': console.warn(`Warning: Server responded unexpectedly:\n${error}`);
                console.log("Error ACS-304")
                onFailure(MESSAGE_FAILURE, undefined);
                if (sessionStorage.ccaasActiveEngagements || sessionStorage.ccaasClientSessionId) {
                    console.warn(`Warning: SDK session / engagements expired.`);
                    cleanSessionStorage()
                    console.warn(`Warning: SDK engagement cache cleared.`);
                    newMsgCtxt?.addMessage('Cleared cache.', MSGPartyType.UI);
                    newMsgCtxt?.addMessage('Reattempting...', MSGPartyType.UI, true);
                    initializeSession(user, config, );
                }
                break;

            case 'ACS-305': console.error(`Error: System is not authorized:\n${error}`);
                onFailure(MESSAGE_UNAUTHORIZED_SYSTEM, stage.state);
                break;

            case 'ACS-321': console.warn(`Warning: SDK init/shutdown is in progress:\n${error}`);
                console.warn('SDK ACS-321:', stage.state);
                break;

            default:
                console.error(`Error: Failed to initialize SDK:\n${error}`);
                onFailure(MESSAGE_FAILURE, undefined);
                break;
        }
    }

    async function initializeSession(

        user: UserDefaultType,
        config: EnvironmentConfig,
        reengage: boolean = false
    ) {
        // CONTEXTS

        processingCtxt?.setIsBusy(true);
        processingCtxt?.setIsFrozen(false);
        newMsgCtxt?.addMessage(`Authorizing a new session...`, MSGPartyType.UI, true)

        console.debug('SDK init initiated:', stage.state);

        try {
            // AUTHORIZE USER

            if (!user) return onFailure(MESSAGE_UNAUTHORIZED_USER, SDKStageType.AUTH_DENIED);

            stage.setState(SDKStageType.AUTH_STARTED);

            const responseJWT = await getAuthResponseJwt(config, user)
            const { jwt, integrationId } = responseJWT;
            if (!jwt || !integrationId) return;

            if (responseJWT.user) {
                user = responseJWT.user;
            }

            console.debug(`Received\njwt:\n${jwt}\nintegrationId:\n${integrationId}`);

            console.debug('SDK authorized:', stage.state);
            stage.setState(SDKStageType.AUTH_FINISHED);

            // Initializing SDK hooks
            const sdkHooks = SDKHooks(user, config, reengage)

            sdkHooks.initialization()
            sdkHooks.messaging()
            sdkHooks.participants()
            sdkHooks.engagement()
            sdkHooks.session()
            sdkHooks.auth()
            sdkHooks.shutdown()


            console.debug('SDK init requested:', stage.state);

            if (!reengage) newMsgCtxt?.addMessage(`Initializing a new session...`, MSGPartyType.UI, true)
            stage.setState(SDKStageType.INIT_STARTED);

            stage.setState(SDKStageType.INIT_REQUESTED);
            await AvayaCCaaSChatSDK.init(getParams(config.url.axp.base, jwt, integrationId), getConfig());
            stage.setState(SDKStageType.INIT_EXECUTED);

            console.debug('SDK init executed:', stage.state);

        } catch (err: any) {
            handleSDKError(err, stage,  config, user)
        }
    }
    // UI functions
    function onFailure(message: string, state = SDKStageType.INIT_FAILED,  ) {
        console.warn(`Warning: SDK process failed:`, message);
        newMsgCtxt?.addMessage(message, MSGPartyType.UI, true);
        setBusy(false);
        processingCtxt?.setIsFrozen(true);
        stage.setState(state);
        return;
    }

    function setBusy(state: boolean) {
        // isBusy = state;
        processingCtxt?.setIsBusy(state);
    }

    // Engagement functions 


    // language functions

    function needToTranslate() {
        console.log(`Translation ${languageToggle} - user: ${languages?.user} |  Agent:  ${languages?.agent}`);
        return languageToggle && languages?.user!== languages.agent
    }

    function recoverLanguageFromSession() {
        const languageSessionStr = sessionStorage.getItem("chat-widget-language")
        if (!languageSessionStr || languageSessionStr === '') return
        try {
            const languageSession = JSON.parse(languageSessionStr) as LanguageSession
            if (languageSession) {
                languages.user = languageSession.UserLanguage
                languages.agent = languageSession.AgentLanguage
                languageToggle = languageSession.status

            }
        } catch (error: any) {
            console.log("error trying to recover language From session")
        }
    }

    // Participant functions
    function addParticipant(userContext: SDKUserContext) {
        newMsgCtxt?.updateParticipant(userContext, true)
        if (participants.length > 0) {
            statusCtxt?.setAgent(`${participants.join(", ")} and ${userContext.participant.displayName}`);
        } else {
            statusCtxt?.setAgent(userContext.participant.displayName);
        }
        participants.push(userContext.participant.displayName)
    }
    function removeParticipant(userContext: SDKUserContext,) {
        const index = participants.indexOf(userContext.participant.displayName);
        if (index > -1) {
            participants.splice(index, 1);
        }
        if (participants.length === 0) {
            newMsgCtxt?.updateParticipant(userContext, false);
            updateTranslationStatus(false)
            statusCtxt?.setAgent(userContext.participant.displayName);
        } else {
            newMsgCtxt?.updateParticipant(userContext, false);
            statusCtxt?.setAgent(participants.join(", "));
        }

    }


    function updateConversationStatus(reengage: boolean) {
        if (conversation.hasStarted) return;

        conversation.start(statusCtxt);
        if (!reengage) {
            // newMsgCtxt.clearDialog(true);
            setBusy(false);
        }
    }


    async function refreshJWT(config: EnvironmentConfig, user: UserDefaultType) {
        if (!user) return onFailure(MESSAGE_UNAUTHORIZED_USER, SDKStageType.AUTH_DENIED);

        const { jwt } = await authenticate(config, user);
        if (!jwt) return;

        AvayaCCaaSChatSDK.setJwtToken(jwt);
    }

    function isAuthJwt(response: AuthResponse): response is AuthResponseJwt {
        return Object.keys(response).includes('jwt');
    }

    async function authenticate(config: EnvironmentConfig, user: UserDefaultType): Promise<AuthResponseJwt> {
        const response = await getAuth(config.url.api.auth, user);
        response.displayMessage && onFailure(response.displayMessage, SDKStageType.AUTH_DENIED);
        if (isAuthJwt(response)) {
            response.timestamp = Date.now();
            sessionStorage.setItem("chat-widget-jwt", JSON.stringify(response))
            return response;
        }
        else return {
            jwt: '',
            integrationId: '',
            ttl: 0,
            displayMessage: response.displayMessage
        };
    }

    async function getAuthResponseJwt(config: EnvironmentConfig, user: UserDefaultType): Promise<AuthResponseJwt> {
        const auth_stored = sessionStorage.getItem("chat-widget-jwt");
        if (auth_stored) {
            try {
                const auth_json: AuthResponseJwt = JSON.parse(auth_stored);
                const timestamp = (auth_json.timestamp || 0) + auth_json.ttl * 1000 * 60;

                if (timestamp > Date.now()) {
                    return auth_json;
                }

                if (auth_json.ttl > Date.now()) return Promise.resolve(auth_json);
            } catch (error: any) {
                return authenticate(config, user);
            }
        }
        return authenticate(config, user);
    }
    function clearSDKListeners() {
        const listeners = [
            'INITIALIZED',
            'TOKEN_EXPIRY_REMINDER',
            'TOKEN_EXPIRED',
            'MESSAGE_ARRIVED',
            'MESSAGE_DELIVERED',
            'EVENT_STREAM_CONNECTING',
            'EVENT_STREAM_CONNECTED',
            'EVENT_STREAM_CLOSED',
            'EVENT_STREAM_FAILED',
            'IDLE_TIMEOUT_EXPIRED',
            'PARTICIPANT_ADDED',
            'PARTICIPANT_DISCONNECTED',
            'ENGAGEMENT_ACTIVE',
            'ENGAGEMENT_ERROR',
            'ENGAGEMENT_TERMINATED',
            'SHUTDOWN'
        ];
        listeners.forEach(listener => {
            AvayaCCaaSChatSDK.off(listener);
        });
    }

    function cleanSessionStorage() {
        sessionStorage.removeItem('ccaasActiveEngagements');
        sessionStorage.removeItem('ccaasClientSessionId');
    }

    async function beforeunload(e: any) {

        omnipageCtxt?.removeTabEngagement()
        await shutdownSDK();
     };
    
     
    return (
        <SDKContext.Provider value={ {  restartSDK, shutdownSDK, initializeSDK } }>
            { children }
        </SDKContext.Provider>
    );


}
